import {
  WaveCloudAPI,
  parseOutputTextFromMissionSteps,
  parseOutputTextFromProviderUsage,
  type WhaleMissionStep,
} from "@vida/wave-core";
import { downloadBlobText } from "./waveBlobDownload";
import type { WaveRagChunk } from "./waveRag";
import { extractAIJobOutputText } from "../utils/waveAssistantInline";
import { createWaveApiConfig } from "./waveSession";

const TERMINAL = new Set(["COMPLETED", "FAILED", "CANCELLED", "TIMED_OUT"]);

export function formatAIJobDiagnostic(job: {
  jobId?: string;
  status?: string;
  errorMessage?: string | null;
  errorCode?: string | null;
  outputAssetKey?: string | null;
  providerUsageJson?: string | null;
}): string {
  const parts: string[] = [];
  if (job.jobId) parts.push(`jobId=${job.jobId}`);
  if (job.status) parts.push(`status=${job.status}`);
  if (job.errorCode) parts.push(`errorCode=${job.errorCode}`);
  if (job.outputAssetKey) parts.push(`outputAssetKey=${job.outputAssetKey}`);
  if (job.providerUsageJson) {
    parts.push(`providerUsage=${job.providerUsageJson.slice(0, 240)}`);
  }
  if (job.errorMessage) parts.push(`errorMessage=${job.errorMessage}`);
  return parts.join(" · ");
}

export async function resolveJobText(job: {
  providerUsageJson?: string | null;
  outputAssetKey?: string | null;
  jobId?: string;
  status?: string;
  errorMessage?: string | null;
}, steps?: WhaleMissionStep[]): Promise<string> {
  const resolved = await resolveWhaleJobOutputText(job, steps);
  if (resolved) return resolved;
  throw new Error("Output assistente non disponibile.", {
    cause: formatAIJobDiagnostic(job),
  });
}

/** Cascade: providerUsage inline → S3 outputAssetKey → mission complete step. */
export async function resolveWhaleJobOutputText(
  job: {
    providerUsageJson?: string | null;
    outputAssetKey?: string | null;
  },
  steps?: WhaleMissionStep[],
): Promise<string | null> {
  const inline =
    extractAIJobOutputText(job.providerUsageJson) ||
    parseOutputTextFromProviderUsage(job.providerUsageJson);
  if (inline) return inline;

  const key = job.outputAssetKey?.trim();
  if (key) {
    try {
      const text = await downloadBlobText(key);
      if (text.trim()) return text.trim();
    } catch {
      /* owner mismatch or transient — fall through to mission steps */
    }
  }

  if (steps?.length) {
    return parseOutputTextFromMissionSteps(steps);
  }
  return null;
}

/** Poll an AI job until completion or timeout. */
export async function pollAIJob(jobId: string, timeoutMs = 120_000): Promise<string> {
  const cfg = createWaveApiConfig();
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    const job = await WaveCloudAPI.getAIJob(cfg, jobId);
    if (TERMINAL.has(job.status)) {
      if (job.status !== "COMPLETED") {
        throw new Error(job.errorMessage ?? `Job ${job.status}`, {
          cause: formatAIJobDiagnostic({ ...job, jobId }),
        });
      }
      try {
        return await resolveJobText({ ...job, jobId });
      } catch (e) {
        const message = e instanceof Error ? e.message : "Output assistente non disponibile.";
        throw new Error(message, { cause: formatAIJobDiagnostic({ ...job, jobId }) });
      }
    }
    await new Promise((r) => setTimeout(r, 1500));
  }
  throw new Error("Timeout assistente.", { cause: `jobId=${jobId}` });
}

export function buildContextFromChunks(chunks: WaveRagChunk[]): string {
  if (!chunks.length) return "(Nessun estratto disponibile per questo documento.)";
  return chunks
    .map((c, i) => {
      const cite = c.fileName ? `[${c.fileName}]` : `[doc ${i + 1}]`;
      return `${cite}\n${c.text}`.trim();
    })
    .join("\n\n---\n\n");
}
